import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string } from "../lazy-zod.js";
//#region src/schemas/local-components.ts
const LOCAL_COMPONENTS_SCHEMA_VERSION = 1;
const nonEmptyStringSchema = string().trim().min(1);
const nullableNonEmptyStringSchema = nonEmptyStringSchema.nullable();
const localComponentStatusSchema = enumFactory([
	"ok",
	"update_available",
	"update_required"
]);
const localComponentUpdatePolicySchema = enumFactory([
	"ok",
	"available",
	"required"
]);
const localComponentUpdateStatusSchema = enumFactory([
	"current",
	"update_available",
	"update_required",
	"unknown"
]);
const openMeldToolsUpdateOperationPhaseSchema = enumFactory([
	"waiting_for_work",
	"updating",
	"updated",
	"restored",
	"needs_help"
]);
const openMeldToolsUpdateRequestSourceSchema = enumFactory([
	"automatic",
	"center_agent",
	"cli",
	"web"
]);
const OPEN_MELD_TOOLS_UPDATE_FAILURE_DETAIL_MAX_CHARS = 2048;
const openMeldToolsUpdateFailureCodeSchema = enumFactory([
	"cli.update.download_stalled",
	"cli.update.download_timed_out",
	"cli.update.download_failed",
	"cli.update.integrity_check_failed",
	"cli.update.install_failed",
	"cli.update.service_alignment_failed",
	"cli.update.failed"
]);
const openMeldToolsUpdateProcessFailureFrameSchema = strictObject({
	detail: nonEmptyStringSchema.max(OPEN_MELD_TOOLS_UPDATE_FAILURE_DETAIL_MAX_CHARS),
	failureCode: openMeldToolsUpdateFailureCodeSchema,
	schema: literal("openmeld-tools-update-process-failure-v1"),
	v: literal(1)
});
const openMeldToolsUpdateOperationSchema = strictObject({
	operationId: nonEmptyStringSchema,
	phase: openMeldToolsUpdateOperationPhaseSchema,
	requestSource: openMeldToolsUpdateRequestSourceSchema,
	requestedAt: iso.datetime({ offset: true }),
	targetCliVersion: nonEmptyStringSchema.optional(),
	targetServiceVersion: nonEmptyStringSchema.optional(),
	targetVersion: nonEmptyStringSchema,
	updatedAt: iso.datetime({ offset: true }),
	urgency: enumFactory(["available", "required"])
});
function resolveOpenMeldToolsUpdateTargetVersions(operation) {
	return {
		cliVersion: operation.targetCliVersion ?? operation.targetVersion,
		serviceVersion: operation.targetServiceVersion ?? operation.targetVersion
	};
}
const openMeldToolsMaintenanceProjectionSchema = strictObject({
	automaticUpdatesEnabled: boolean(),
	status: enumFactory(["ready", "needs_help"])
});
const openMeldToolsControlCapabilitySchema = strictObject({
	requestAutomaticUpdate: literal(true).optional(),
	requestUpdate: literal(true).optional(),
	setAutomaticUpdates: literal(true).optional()
});
const openMeldToolsUpdateRequestResultSchema = discriminatedUnion("status", [
	strictObject({ status: literal("disabled") }),
	strictObject({ status: literal("unavailable") }),
	strictObject({
		currentVersion: nonEmptyStringSchema,
		status: literal("current")
	}),
	strictObject({
		operation: openMeldToolsUpdateOperationSchema,
		status: enumFactory(["started", "already_running"])
	})
]);
const openMeldToolsMaintenanceMutationResultSchema = strictObject({
	automaticUpdatesEnabled: boolean(),
	status: enumFactory(["updated", "unchanged"])
});
const openMeldToolsControlRequestSchema = discriminatedUnion("operation", [strictObject({
	operation: literal("request_update"),
	requestSource: enumFactory([
		"automatic",
		"center_agent",
		"web"
	])
}), strictObject({
	automaticUpdatesEnabled: boolean(),
	operation: literal("set_automatic_updates"),
	requestSource: enumFactory(["center_agent", "web"])
})]);
const openMeldToolsControlResultSchema = discriminatedUnion("operation", [strictObject({
	operation: literal("request_update"),
	result: openMeldToolsUpdateRequestResultSchema
}), strictObject({
	operation: literal("set_automatic_updates"),
	result: openMeldToolsMaintenanceMutationResultSchema
})]);
const openMeldToolsUpdateAttemptSchema = strictObject({
	v: literal(1),
	attempts: number().int().nonnegative(),
	errorMessage: nullableNonEmptyStringSchema,
	operation: openMeldToolsUpdateOperationSchema
});
const localSetupActionSchema = enumFactory([
	"none",
	"connect_service",
	"reconnect_service",
	"update_service",
	"connect_local_agent",
	"update_skills",
	"update_cli"
]);
const localSetupEntrypointSchema = literal("setup");
const localComponentsManifestSchema = strictObject({
	schemaVersion: literal(1),
	openMeldReleaseVersion: nonEmptyStringSchema,
	cli: strictObject({
		packageVersion: nonEmptyStringSchema,
		commandContractVersion: nonEmptyStringSchema
	}),
	service: strictObject({
		bundleHash: nullableNonEmptyStringSchema,
		bundleVersion: nonEmptyStringSchema,
		runtimeContractEpoch: nonEmptyStringSchema,
		serviceContractVersion: nonEmptyStringSchema
	}),
	skills: strictObject({
		bundleHash: nullableNonEmptyStringSchema,
		skillContractVersion: nonEmptyStringSchema,
		skillIds: array(nonEmptyStringSchema)
	})
});
const localComponentsReleaseManifestSchema = strictObject({
	schemaVersion: literal(1),
	updatedAt: iso.datetime({ offset: true }),
	components: strictObject({
		cli: strictObject({
			packageVersion: nonEmptyStringSchema,
			commandContractVersion: nonEmptyStringSchema,
			updatePolicy: localComponentUpdatePolicySchema,
			reason: nonEmptyStringSchema
		}),
		service: strictObject({
			bundleHash: nullableNonEmptyStringSchema,
			bundleVersion: nonEmptyStringSchema,
			runtimeContractEpoch: nonEmptyStringSchema,
			serviceContractVersion: nonEmptyStringSchema,
			updatePolicy: localComponentUpdatePolicySchema,
			reason: nonEmptyStringSchema
		}),
		skills: strictObject({
			bundleHash: nullableNonEmptyStringSchema,
			skillContractVersion: nonEmptyStringSchema,
			skillIds: array(nonEmptyStringSchema),
			updatePolicy: localComponentUpdatePolicySchema,
			reason: nonEmptyStringSchema
		})
	})
});
const installedLocalComponentsSnapshotSchema = strictObject({
	schemaVersion: literal(1),
	installedAt: iso.datetime({ offset: true }),
	source: enumFactory([
		"setup",
		"repair",
		"manual"
	]),
	manifest: localComponentsManifestSchema
});
const localComponentProjectionSchema = strictObject({
	reasonCodes: array(nonEmptyStringSchema),
	status: localComponentStatusSchema
});
const localCliComponentProjectionSchema = localComponentProjectionSchema.extend({
	commandContractVersion: nonEmptyStringSchema,
	latestPackageVersion: nullableNonEmptyStringSchema,
	packageVersion: nonEmptyStringSchema,
	requiredCommandContractVersion: nullableNonEmptyStringSchema
});
const localServiceComponentProjectionSchema = localComponentProjectionSchema.extend({
	expectedBundleHash: nullableNonEmptyStringSchema,
	expectedBundleVersion: nonEmptyStringSchema,
	installedBundleHash: nullableNonEmptyStringSchema,
	installedBundleVersion: nullableNonEmptyStringSchema,
	runtimeContractEpoch: nonEmptyStringSchema,
	serviceContractVersion: nonEmptyStringSchema
});
const localSkillsComponentProjectionSchema = localComponentProjectionSchema.extend({
	contentRelease: strictObject({
		revision: number().int().positive(),
		minimumCliVersion: string().regex(/^\d+\.\d+\.\d+$/)
	}).optional(),
	expectedBundleHash: nullableNonEmptyStringSchema,
	installedBundleHash: nullableNonEmptyStringSchema,
	skillContractVersion: nonEmptyStringSchema,
	targetCount: number().int().nonnegative()
});
const localComponentsStatusSchema = strictObject({
	schemaVersion: literal(1),
	generatedAt: iso.datetime({ offset: true }),
	entrypoint: localSetupEntrypointSchema,
	summaryLabel: nonEmptyStringSchema,
	summaryDescription: nonEmptyStringSchema,
	setupAction: localSetupActionSchema,
	setupActionLabel: nonEmptyStringSchema,
	setupActionDescription: nonEmptyStringSchema,
	maintenance: openMeldToolsMaintenanceProjectionSchema.optional(),
	updateOperation: openMeldToolsUpdateOperationSchema.optional(),
	cli: localCliComponentProjectionSchema,
	service: localServiceComponentProjectionSchema,
	skills: localSkillsComponentProjectionSchema
});
const localServiceComponentUpdateProjectionSchema = strictObject({
	installedVersion: nullableNonEmptyStringSchema,
	latestVersion: nullableNonEmptyStringSchema,
	reasonCodes: array(nonEmptyStringSchema),
	requiredVersion: nullableNonEmptyStringSchema,
	status: localComponentUpdateStatusSchema,
	updatePolicy: localComponentUpdatePolicySchema,
	wakeBlocked: boolean()
});
const localCliComponentUpdateProjectionSchema = strictObject({
	commandContractVersion: nullableNonEmptyStringSchema,
	installedPackageVersion: nullableNonEmptyStringSchema,
	latestPackageVersion: nullableNonEmptyStringSchema,
	reasonCodes: array(nonEmptyStringSchema),
	requiredCommandContractVersion: nullableNonEmptyStringSchema,
	status: localComponentUpdateStatusSchema,
	updatePolicy: localComponentUpdatePolicySchema,
	wakeBlocked: boolean()
});
const localSkillsComponentUpdateProjectionSchema = strictObject({
	expectedBundleHash: nullableNonEmptyStringSchema,
	expectedTargetCount: number().int().nonnegative(),
	installedBundleHash: nullableNonEmptyStringSchema,
	reasonCodes: array(nonEmptyStringSchema),
	skillContractVersion: nullableNonEmptyStringSchema,
	status: localComponentUpdateStatusSchema,
	targetCount: number().int().nonnegative().nullable(),
	updatePolicy: localComponentUpdatePolicySchema,
	wakeBlocked: boolean()
});
const localComponentUpdateProjectionSchema = strictObject({
	schemaVersion: literal(1),
	generatedAt: iso.datetime({ offset: true }),
	setupAction: localSetupActionSchema,
	setupActionLabel: nonEmptyStringSchema,
	setupActionDescription: nonEmptyStringSchema,
	humanSetupCommand: nonEmptyStringSchema,
	agentSetupCommand: nonEmptyStringSchema,
	maintenance: openMeldToolsMaintenanceProjectionSchema.optional(),
	updateOperation: openMeldToolsUpdateOperationSchema.optional(),
	cli: localCliComponentUpdateProjectionSchema,
	service: localServiceComponentUpdateProjectionSchema,
	skills: localSkillsComponentUpdateProjectionSchema
});
function formatLocalSetupActionLabel(action) {
	switch (action) {
		case "connect_service": return "Connect Computer";
		case "reconnect_service": return "Reconnect Computer";
		case "update_service": return "Update OpenMeld on Computer";
		case "connect_local_agent": return "Connect local agent";
		case "update_skills": return "Update OpenMeld skills";
		case "update_cli": return "Update OpenMeld CLI";
		case "none": return "Everything is up to date";
		default: return action;
	}
}
function formatOpenMeldToolsUpdateFailureMessage(code) {
	switch (code) {
		case "cli.update.download_stalled": return "The update download stopped making progress. Check this computer's connection and try again.";
		case "cli.update.download_timed_out": return "The update download exceeded its recovery time. Check this computer's connection and try again.";
		case "cli.update.download_failed": return "The update could not be downloaded. Check this computer's connection and try again.";
		case "cli.update.integrity_check_failed": return "The downloaded update did not pass integrity checks, so it was not installed.";
		case "cli.update.install_failed": return "The update could not be installed.";
		case "cli.update.service_alignment_failed": return "OpenMeld Service could not finish updating.";
		case "cli.update.failed": return "The update could not be completed. Try again or update manually.";
		default: return code;
	}
}
function findOpenMeldToolsUpdateFailureCode(reasonCodes) {
	for (const reasonCode of reasonCodes) {
		const parsed = openMeldToolsUpdateFailureCodeSchema.safeParse(reasonCode);
		if (parsed.success) return parsed.data;
	}
	return null;
}
function formatLocalComponentsSummaryLabel(input) {
	if (input.setupAction !== "none") return formatLocalSetupActionLabel(input.setupAction);
	switch (input.cliStatus) {
		case "update_required": return "Update OpenMeld CLI";
		case "update_available": return "OpenMeld CLI update available";
		case "ok": return "Everything is up to date";
		default: return input.cliStatus;
	}
}
function formatLocalComponentsSummaryDescription(input) {
	if (input.setupAction !== "none") return formatLocalSetupActionDescription(input.setupAction);
	switch (input.cliStatus) {
		case "update_required": return "Update OpenMeld CLI before running this setup flow.";
		case "update_available": return "OpenMeld CLI can be updated, but this setup flow can continue.";
		case "ok": return "OpenMeld local components are aligned for this computer.";
		default: return input.cliStatus;
	}
}
function formatLocalSetupActionDescription(action) {
	switch (action) {
		case "connect_service": return "Connect this computer to OpenMeld so local agents can reply in Spaces.";
		case "reconnect_service": return "Reconnect this computer to the current OpenMeld account.";
		case "update_service": return "Update OpenMeld on this computer.";
		case "connect_local_agent": return "Connect this Agent Profile to a local agent runtime on this computer.";
		case "update_skills": return "Update OpenMeld skills through setup on this computer.";
		case "update_cli": return "Update OpenMeld CLI before running this setup flow.";
		case "none": return "OpenMeld local components are aligned for this computer.";
		default: return action;
	}
}
//#endregion
export { LOCAL_COMPONENTS_SCHEMA_VERSION, OPEN_MELD_TOOLS_UPDATE_FAILURE_DETAIL_MAX_CHARS, findOpenMeldToolsUpdateFailureCode, formatLocalComponentsSummaryDescription, formatLocalComponentsSummaryLabel, formatLocalSetupActionDescription, formatLocalSetupActionLabel, formatOpenMeldToolsUpdateFailureMessage, installedLocalComponentsSnapshotSchema, localCliComponentProjectionSchema, localCliComponentUpdateProjectionSchema, localComponentProjectionSchema, localComponentStatusSchema, localComponentUpdatePolicySchema, localComponentUpdateProjectionSchema, localComponentUpdateStatusSchema, localComponentsManifestSchema, localComponentsReleaseManifestSchema, localComponentsStatusSchema, localServiceComponentProjectionSchema, localServiceComponentUpdateProjectionSchema, localSetupActionSchema, localSetupEntrypointSchema, localSkillsComponentProjectionSchema, localSkillsComponentUpdateProjectionSchema, openMeldToolsControlCapabilitySchema, openMeldToolsControlRequestSchema, openMeldToolsControlResultSchema, openMeldToolsMaintenanceMutationResultSchema, openMeldToolsMaintenanceProjectionSchema, openMeldToolsUpdateAttemptSchema, openMeldToolsUpdateFailureCodeSchema, openMeldToolsUpdateOperationPhaseSchema, openMeldToolsUpdateOperationSchema, openMeldToolsUpdateProcessFailureFrameSchema, openMeldToolsUpdateRequestResultSchema, openMeldToolsUpdateRequestSourceSchema, resolveOpenMeldToolsUpdateTargetVersions };

//# sourceMappingURL=local-components.js.map